import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDropzone, DropzoneRootProps, DropzoneInputProps } from 'react-dropzone'
import toast from 'react-hot-toast'
import { lettersApi } from '@/lib/api'
import { getErrorMessage } from '@/utils/errorHandling'
import { triggerEvent } from '@/services/tracking'
import { useConfig } from '@/hooks/useConfig'
import type { LetterUpload, SenderInfo } from '@/types'

export default function UploadPage() {
  const navigate = useNavigate()
  const { config } = useConfig()
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState<LetterUpload>({
    title: '',
    content: '',
    tags: [],
    sender_info: {
      name: '',
      location: '',
      date: new Date().toISOString().split('T')[0],
    },
    recipient_info: {
      name: '',
      location: '',
    },
    is_published: false,
  })
  const [tagInput, setTagInput] = useState('')
  const [file, setFile] = useState<File | null>(null)

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setFile(acceptedFiles[0])
      }
    },
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
      'application/pdf': ['.pdf'],
      'text/*': ['.txt', '.md'],
    },
    maxSize: config.max_file_size,
    multiple: false,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.title) {
      toast.error('Title is required')
      return
    }

    if (!file && !formData.content) {
      toast.error('Please provide either a file or content')
      return
    }

    setIsLoading(true)

    try {
      const uploadData: LetterUpload = {
        ...formData,
        file: file || undefined,
      }

      const result = await lettersApi.create(uploadData)

      // Track successful upload
      triggerEvent('letter_upload', {
        letter_id: result.id,
        has_file: !!file,
        has_content: !!formData.content,
        tags_count: formData.tags?.length || 0,
      })

      // Check if upload was queued for retry (LetterUploadResponse has a message field)
      if ('message' in result && !('created_at' in result)) {
        toast.success(result.message || 'Upload queued for processing.')
        navigate('/letters')
      } else {
        toast.success('Letter uploaded! Text extraction in progress...')
        navigate(`/letters/${result.id}`)
      }
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  const addTag = () => {
    if (tagInput && !formData.tags?.includes(tagInput)) {
      setFormData({
        ...formData,
        tags: [...(formData.tags || []), tagInput],
      })
      setTagInput('')
    }
  }

  const removeTag = (tag: string) => {
    setFormData({
      ...formData,
      tags: formData.tags?.filter((t) => t !== tag) || [],
    })
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold text-olive-900 mb-8">Upload Letter</h1>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* File Upload */}
        <DocumentUploadZone
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          isDragActive={isDragActive}
          file={file}
          onRemove={() => setFile(null)}
          maxFileSizeMB={Math.round(config.max_file_size / 1024 / 1024)}
          crisisMode={config.crisis_mode}
        />

        {/* Basic Information */}
        <div className="card">
          <h2 className="text-xl font-semibold text-olive-900 mb-4">Letter Information</h2>

          <div className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-olive-700 mb-1">
                Title *
              </label>
              <input
                id="title"
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="input-field"
                required
              />
            </div>

            <div>
              <label htmlFor="content" className="block text-sm font-medium text-olive-700 mb-1">
                Content / Transcription
              </label>
              <textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="input-field min-h-[150px]"
                placeholder="Add letter content or transcription..."
              />
            </div>

            <TagManagementSection
              tags={formData.tags || []}
              tagInput={tagInput}
              setTagInput={setTagInput}
              onAddTag={addTag}
              onRemoveTag={removeTag}
            />
          </div>
        </div>

        {/* Sender/Recipient Information */}
        <div className="grid md:grid-cols-2 gap-6">
          <CorrespondentInformationCard
            title="Sender Information"
            data={formData.sender_info}
            onChange={(senderInfo) => setFormData({ ...formData, sender_info: senderInfo })}
            includeDate
          />

          <CorrespondentInformationCard
            title="Recipient Information"
            data={formData.recipient_info}
            onChange={(recipientInfo) =>
              setFormData({ ...formData, recipient_info: recipientInfo })
            }
          />
        </div>

        {/* Publishing Options */}
        <div className="card">
          <h2 className="text-xl font-semibold text-olive-900 mb-4">Publishing Options</h2>

          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={formData.is_published}
              onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
              className="w-4 h-4 text-olive-600 rounded focus:ring-olive-500"
            />
            <span className="text-olive-700">Make this letter publicly visible</span>
          </label>
        </div>

        {/* Submit */}
        <div className="flex justify-end space-x-4">
          <button type="button" onClick={() => navigate('/letters')} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={isLoading} className="btn-primary">
            {isLoading ? 'Uploading...' : 'Upload Letter'}
          </button>
        </div>
      </form>
    </div>
  )
}

interface DocumentUploadZoneProps {
  getRootProps: () => DropzoneRootProps
  getInputProps: () => DropzoneInputProps
  isDragActive: boolean
  file: File | null
  onRemove: () => void
  maxFileSizeMB: number
  crisisMode: boolean
}

function DocumentUploadZone({
  getRootProps,
  getInputProps,
  isDragActive,
  file,
  onRemove,
  maxFileSizeMB,
  crisisMode,
}: DocumentUploadZoneProps) {
  return (
    <div className="card">
      <h2 className="text-xl font-semibold text-olive-900 mb-4">Upload Document</h2>

      {!file ? (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-olive-600 bg-olive-50'
              : 'border-olive-300 hover:border-olive-400'
          }`}
        >
          <input {...getInputProps()} />
          <svg
            className="mx-auto h-12 w-12 text-olive-400 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          <p className="text-olive-700">
            {isDragActive ? 'Drop the file here' : 'Drag & drop a file here, or click to select'}
          </p>
          <p className="text-sm text-olive-500 mt-2">
            Supports images, PDFs, and text files up to {maxFileSizeMB}MB
            {crisisMode ? ' (crisis mode)' : ''}
          </p>
        </div>
      ) : (
        <div className="flex items-center justify-between p-4 bg-olive-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <svg
              className="h-8 w-8 text-olive-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <div>
              <p className="font-medium text-olive-900">{file.name}</p>
              <p className="text-sm text-olive-600">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
          </div>
          <button type="button" onClick={onRemove} className="text-olive-600 hover:text-olive-800">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  )
}

function TagManagementSection({
  tags,
  tagInput,
  setTagInput,
  onAddTag,
  onRemoveTag,
}: {
  tags: string[]
  tagInput: string
  setTagInput: (value: string) => void
  onAddTag: () => void
  onRemoveTag: (tag: string) => void
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-olive-700 mb-1">Tags</label>
      <div className="flex items-center space-x-2 mb-2">
        <input
          type="text"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onAddTag()
            }
          }}
          className="flex-1 input-field"
          placeholder="Add tags..."
        />
        <button type="button" onClick={onAddTag} className="btn-secondary">
          Add
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-olive-100 text-olive-800"
          >
            {tag}
            <button
              type="button"
              onClick={() => onRemoveTag(tag)}
              className="ml-2 text-olive-600 hover:text-olive-800"
            >
              ×
            </button>
          </span>
        ))}
      </div>
    </div>
  )
}

interface CorrespondentInformationCardProps {
  title: string
  data: SenderInfo | undefined
  onChange: (data: SenderInfo) => void
  includeDate?: boolean
}

function CorrespondentInformationCard({
  title,
  data,
  onChange,
  includeDate = false,
}: CorrespondentInformationCardProps) {
  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-olive-900 mb-3">{title}</h3>
      <div className="space-y-3">
        <input
          type="text"
          value={data?.name || ''}
          onChange={(e) => onChange({ ...data, name: e.target.value })}
          className="input-field"
          placeholder="Name"
        />
        <input
          type="text"
          value={data?.location || ''}
          onChange={(e) => onChange({ ...data, location: e.target.value })}
          className="input-field"
          placeholder="Location"
        />
        {includeDate && (
          <input
            type="date"
            value={data?.date || ''}
            onChange={(e) => onChange({ ...data, date: e.target.value })}
            className="input-field"
          />
        )}
      </div>
    </div>
  )
}
